import {
    collection, doc, setDoc, writeBatch, getDoc,
} from 'firebase/firestore';
import type { User as FirebaseUser } from 'firebase/auth';
import { db } from '../services/firebase';
import type { Language, User, WeekStartDay } from '../types';
import { starterRecipes } from './starterRecipes';
import { getDefaultUserMealPreferences } from './userMealPreferences';
import { getPersonalHouseholdId } from './householdUtils';
import { getTranslation } from './translations';

import { detectBrowserLanguage, detectBrowserWeekStartDay } from './appLanguage';

export async function provisionNewUser(firebaseUser: FirebaseUser): Promise<User> {
    const existing = await getDoc(doc(db, 'users', firebaseUser.uid));
    if (existing.exists()) {
        return existing.data() as User;
    }

    const acceptedTerms = sessionStorage.getItem('acceptedTermsOnRegister') === 'true';
    if (acceptedTerms) {
        sessionStorage.removeItem('acceptedTermsOnRegister');
    }

    const language = detectBrowserLanguage();
    const personalHouseholdId = getPersonalHouseholdId(firebaseUser.uid);

    const newUser: User = {
        uid: firebaseUser.uid,
        email: firebaseUser.email || '',
        role: 'user',
        userType: 'regular',
        accountMode: 'standard',
        activeHouseholdId: personalHouseholdId,
        households: [personalHouseholdId],
        language,
        weekStartDay: detectBrowserWeekStartDay(),
        theme: 'light',
        subscription: {
            status: 'pending',
            startDate: new Date().toISOString(),
            endDate: null,
            plan: 'free',
            planDetails: { price: 0, currency: 'USD', households: 5 },
            adminViewed: false,
        },
        creationDate: new Date().toISOString(),
        updateDate: new Date().toISOString(),
        dietPreference: 'none',
        ...(acceptedTerms ? { acceptedTerms: true } : {}),
    };

    await setDoc(doc(db, 'users', firebaseUser.uid), newUser);
    await setDoc(doc(db, 'userMealPreferences', firebaseUser.uid), getDefaultUserMealPreferences(firebaseUser.uid, newUser));
    await setDoc(doc(db, 'households', personalHouseholdId), {
        id: personalHouseholdId,
        name: getTranslation(language, 'personalHouseholdName'),
        ownerId: firebaseUser.uid,
        members: [firebaseUser.uid],
        memberRoles: { [firebaseUser.uid]: 'owner' },
        residentsCount: 1,
        adultsCount: 1,
        childrenCount: 0,
        visibleMealTypes: ['Breakfast', 'Lunch', 'Dinner'],
    });

    const batch = writeBatch(db);
    starterRecipes.forEach(r => {
        const ref = doc(collection(db, 'recipes'));
        batch.set(ref, {
            ...r,
            ownerId: firebaseUser.uid,
            householdId: personalHouseholdId,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
        });
    });
    await batch.commit();

    return newUser;
}
